import {HorizontalTextAlign, TextWidgetData, VerticalTextAlign} from '@dv5/domain'
import React, {CSSProperties, FC} from 'react'
import {ColorPicker} from '../common/ColorPicker'
import {TextAlignmentPicker} from '../common/TextAlignmentPicker'
import {COLOR_PRESETS} from "../sticky-note/constants"
import {TextFormatPicker} from "./TextFormatPicker"
import {useTextWidgetUpdate} from "./useTextWidgetUpdate"
import './TextWidgetToolbar.less'

interface Props {
    canvasId: string
    widgetId: string
    data: TextWidgetData
    toolbarStyles: CSSProperties
    onExpandToFull?: () => void
}

export const TextWidgetToolbar: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onExpandToFull,
}) => {
    const { updateData } = useTextWidgetUpdate(canvasId, widgetId, data)

    const handleBgColorChange = (color: string) => {
        updateData(['bgColor'], {bgColor: color})
    }

    const handleFgColorChange = (color: string) => {
        updateData(['fgColor'], {fgColor: color})
    }

    const handleHorizontalAlignChange = (horizontalTextAlign: HorizontalTextAlign) => {
        updateData(['textProperties.horizontalTextAlign'], {
            textProperties: {...data.textProperties, horizontalTextAlign}
        })
    }

    const handleVerticalAlignChange = (verticalTextAlign: VerticalTextAlign) => {
        updateData(['textProperties.verticalTextAlign'], {
            textProperties: {...data.textProperties, verticalTextAlign}
        })
    }

    const handleFormatChange = (properties: { bold?: boolean; italic?: boolean; underline?: boolean; lineThrough?: boolean }) => {
        const updatedProperties = {...data.textProperties, ...properties}
        const patchedPaths = Object.keys(properties).map(prop => `textProperties.${prop}`)
        updateData(patchedPaths, {textProperties: updatedProperties})
    }
    const changeExpand = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        if (onExpandToFull) {
            onExpandToFull()
        }
    }

    return (
        <div className='text-widget-toolbar' style={toolbarStyles}>
            <ColorPicker
                value={data.bgColor}
                presets={COLOR_PRESETS}
                onChange={handleBgColorChange}
            />
            <ColorPicker
                value={data.fgColor || '#333333'}
                presets={COLOR_PRESETS}
                onChange={handleFgColorChange}
            />
            <div className='text-widget-toolbar__divider' />
            <TextAlignmentPicker
                horizontalAlign={data.textProperties.horizontalTextAlign}
                verticalAlign={data.textProperties.verticalTextAlign}
                onHorizontalAlignChange={handleHorizontalAlignChange}
                onVerticalAlignChange={handleVerticalAlignChange}
            />
            <div className='text-widget-toolbar__divider' />
            <TextFormatPicker
                prefix="text-widget-toolbar"
                bold={data.textProperties.bold}
                italic={data.textProperties.italic}
                underline={data.textProperties.underline}
                lineThrough={data.textProperties.lineThrough}
                onChange={handleFormatChange}
            />
            {onExpandToFull && (
                <>
                    <div className='text-widget-toolbar__divider' />
                    <button
                        className='text-widget-toolbar__button text-widget-toolbar__button--expand'
                        onMouseDown={changeExpand}
                        aria-label='Expand to full editor'
                        type='button'
                    >
                        ↗
                    </button>
                </>
            )}
        </div>
    )
}
